import { AfterViewChecked, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostBinding, OnInit, PLATFORM_ID, computed, effect, inject, signal, viewChild } from '@angular/core';
import { SectionComponent } from '../../components/section/section.component';
import { Section, SectionAttributes } from '../../types/section';
import { ColumnWidth } from '@knorr-bremse-portals/ngx-components';
import { HtmlComponent } from '@knorr-bremse-portals/ngx-components/components/html';
import { isPlatformServer } from '@angular/common';
import { AdvancedNumberPipe } from '../../providers/advanced-number.pipe';
import { EditorIdDirective } from '../../directives/editor-id.directive';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';

export interface NumberStageAttributes extends SectionAttributes {
  numColumns: ColumnWidth.Narrow;
  startNumber?: number;
  targetNumber: number;
  showNumberSign: boolean;
  currencyCode?: string;
  unit?: string;
  prefix?: string;
  textHtml: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact';
  styleVariant?: 'primary' | 'secondary' | 'secondary-variant' | 'background';
  grouping?: 'always' | 'auto' | 'min2' | false;
}

type NumberStageEditorIds = [
  'startNumber',
  'targetNumber',
  'showNumberSign',
  'currencyCode',
  'unit',
  'textHtml',
  'minimumFractionDigits',
  'styleVariant',
];

export type NumberStageSection = Section<NumberStageAttributes, NumberStageEditorIds, 'number-stage'>;

@Component({
  selector: 'section[app-number-stage]',
  standalone: true,
  imports: [
    HtmlComponent,
    AdvancedNumberPipe,
    EditorIdDirective,
    FsButtonComponent,
],
  templateUrl: './number-stage.component.html',
  styleUrl: './number-stage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberStageComponent extends SectionComponent<NumberStageSection> implements AfterViewChecked, OnInit {

  @HostBinding('attr.style-variant') public get styleVariant(): string | null {
    return this.attributes.styleVariant ?? null;
  }

  protected currentNumber = signal(0);
  protected currentNumberAbsolute = computed(() => {
    return Math.abs(this.currentNumber());
  });
  protected numberElement = viewChild.required<ElementRef<HTMLDivElement>>('numberElement');
  private isServer = isPlatformServer(inject(PLATFORM_ID));

  private numberInViewport = signal(false);
  private destroyRef = inject(DestroyRef);

  protected numberStyle?: 'currency' | 'decimal';

  public constructor() {
    super();
    this.setupCounter();
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.numberStyle = this.attributes.currencyCode ? 'currency' : 'decimal';
    this.currentNumber.set(this.attributes.targetNumber);
  }

  public ngAfterViewChecked(): void {
    this.setupNumberInViewportObserver();
  }

  private setupNumberInViewportObserver(): void {
    if (this.isServer) {
      return;
    }

    const headerHeight = 73; // 73 is the size of the header on desktop while scrolled

    const observer = new IntersectionObserver(([entry]) => {
      this.numberInViewport.set(entry.isIntersecting);
    }, { rootMargin: `${-headerHeight}px 0px 0px 0px` });
    observer.observe(this.numberElement().nativeElement);
    this.destroyRef.onDestroy(() => {
      observer.disconnect();
    });
  }

  private animateCurrentNumber(startNumber: number, targetNumber: number, durationMilliseconds: number): AbortController {
    let startTimestamp: number;
    let animation: number;
    const abortController = new AbortController();
    const step = (timestamp: number) => {
      if (abortController.signal.aborted) {
        window.cancelAnimationFrame(animation);
        return;
      }

      if (!startTimestamp) {
        startTimestamp = timestamp;
      }
      let progress = Math.min((timestamp - startTimestamp) / durationMilliseconds, 1);
      // Apply a cubic easing out function to slow down the animation as it approaches the end
      progress = 1 - Math.pow(1 - progress, 3);
      const value = progress * (targetNumber - startNumber) + startNumber;
      if (this.currentNumber() !== value) {
        this.currentNumber.set(value);
      }
      if (progress < 1) {
        animation = window.requestAnimationFrame(step);
      }
    };
    animation = window.requestAnimationFrame(step);
    return abortController;
  }

  private setupCounter(): void {
    effect(() => {
      if (this.numberInViewport() === false) {
        this.currentNumber.set(this.attributes.startNumber ?? 0);
        return;
      }

      this.animateCurrentNumber(this.attributes.startNumber ?? 0, this.attributes.targetNumber, 2000);
    }, { allowSignalWrites: true });
  }

}
